/**
 * 사업자등록번호 체크섬 검증 유틸
 *
 * 한국 국세청의 사업자등록번호 마지막 자리는 체크섬이며,
 * 다음 알고리즘으로 검증할 수 있다 (공식 검증 공식).
 *
 *   가중치: [1, 3, 7, 1, 3, 7, 1, 3, 5]
 *   1) 9자리 각 숫자 × 가중치 합
 *   2) (9번째 숫자 × 5) / 10 의 몫을 추가
 *   3) 합 % 10 == 0 이면 마지막 자리는 0
 *      그렇지 않으면 마지막 자리는 (10 - 합 % 10)
 *
 * 이 검증은 1차 방어선이다:
 *   - 통과: "그럴듯한 번호" (실제 존재 여부는 모름)
 *   - 실패: "확실히 가짜" (예: 111-11-11111, 999-99-99999)
 *
 * 더 확실한 검증은 국세청 진위확인 API (verifyBusinessNumber.ts) 사용.
 */

/**
 * 사업자등록번호 체크섬 검증
 *
 * @param raw  "000-00-00000" 또는 "0000000000" 형태
 * @returns true 면 체크섬 통과 (실제 존재 여부는 별개)
 *
 * 예:
 *   isValidBusinessNumberChecksum('608-88-01043') → true
 *   isValidBusinessNumberChecksum('111-11-11111') → false
 *   isValidBusinessNumberChecksum('999-99-99999') → false
 */
export function isValidBusinessNumberChecksum(raw: string): boolean {
  const digits = (raw ?? '').replace(/\D/g, '');
  if (digits.length !== 10) return false;

  // 명백한 더미 패턴 거르기 (체크섬 알고리즘과 별개로 추가 가드)
  // 모두 같은 숫자, 또는 0으로만 구성 — 운영상 정상 데이터일 수 없음
  if (/^(\d)\1{9}$/.test(digits)) return false;
  if (digits === '0000000000') return false;

  const weights = [1, 3, 7, 1, 3, 7, 1, 3, 5];
  let sum = 0;
  for (let i = 0; i < 9; i += 1) {
    sum += Number(digits[i]) * weights[i]!;
  }
  // 9번째 자리(index 8) × 5 의 십의 자리 추가
  sum += Math.floor((Number(digits[8]) * 5) / 10);

  const checksum = (10 - (sum % 10)) % 10;
  return checksum === Number(digits[9]);
}

/**
 * 체크섬 검증 결과를 사람이 읽을 수 있는 메시지로
 *
 * @returns null 이면 정상, 문자열이면 거부 사유
 */
export function getBusinessNumberError(raw: string): string | null {
  const digits = (raw ?? '').replace(/\D/g, '');
  if (digits.length === 0) return '사업자등록번호를 입력해 주세요';
  if (digits.length !== 10) return '사업자등록번호 10자리를 정확히 입력해 주세요';
  if (!isValidBusinessNumberChecksum(digits)) {
    return '올바른 사업자등록번호가 아닙니다. 다시 확인해 주세요.';
  }
  return null;
}
